import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/button/button'
import SvgIcon from '@/components/svg-icon/svgIcon'
import { Typography } from '@/components/typography/typography'
import { CrossSellingBanner } from '@/features/cross-selling'
import { useTravelPeriodQuery } from '@/features/url-handling'
import { cn } from '@/libs/tw'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { useStepStore } from '@/stores/step-store/stepStore'
import { translate } from '@/utils/translator'

import { useHotelMessages } from '../hooks/useHotelMessages'
import { TravelPeriodCalendar } from './travelPeriodCalendar'
import { TravelPeriodDateSummary } from './travelPeriodDateSummary'
import { TravelPeriodHolidays } from './travelPeriodHolidays'

export const TravelPeriodContent = () => {
	useTravelPeriodQuery()

	const [isMinNightValid, setIsMinNightValid] = useState(true)
	const [showScrollIndicator, setShowScrollIndicator] = useState(false)
	const [calendarIsVisible, setCalendarIsVisible] = useState(false)
	const setNextStep = useStepStore.use.setNextStep()
	const { startDate, endDate, showCrossSelling, nights, federalState } =
		useCartStore.use.travelPeriod()
	const { code } = useCartStore.use.promotion()
	const hotelMessages = useHotelMessages()
	const calendarRef = useRef<HTMLDivElement>(null)
	const crossSellBannerRef = useRef<HTMLDivElement>(null)
	const proceedBtnRef = useRef<HTMLDivElement>(null)

	const isStepValid = !showCrossSelling && startDate && endDate && isMinNightValid

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					setShowScrollIndicator(false)
				} else {
					if (entries[0].boundingClientRect.top > 0) {
						setShowScrollIndicator(true)
					} else {
						setShowScrollIndicator(false)
					}
				}
			},
			{
				threshold: 0.5,
			}
		)

		if (proceedBtnRef.current) {
			observer.observe(proceedBtnRef.current)
		}

		if (showCrossSelling && crossSellBannerRef.current) {
			observer.observe(crossSellBannerRef.current)
		}

		return () => observer.disconnect()
	}, [isStepValid, showCrossSelling])

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					setCalendarIsVisible(true)
				} else {
					setCalendarIsVisible(false)
				}
			},
			{
				threshold: 0.3,
			}
		)

		if (calendarRef.current) {
			observer.observe(calendarRef.current)
		}

		return () => observer.disconnect()
	}, [isStepValid])

	return (
		<article className={cn('flex flex-col gap-24')}>
			<div
				className={cn(
					'flex w-full flex-col  gap-32 border-t border-textColor bg-primary pt-24 text-textColor md:gap-24',
					'md:rounded-8 md:border-none md:bg-naturel md:p-24 md:pb-32 md:text-travelPeriod-text'
				)}
			>
				<Typography element="h3" style="h3">
					{translate('travel-period-your-selected-travelperiod')}
				</Typography>
				<div
					onClick={() =>
						calendarRef.current?.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
						})
					}
				>
					<TravelPeriodDateSummary
						startDate={startDate}
						endDate={endDate}
						nights={nights}
						inverted
					/>
				</div>
				<div className="mr-[auto] md:hidden">
					<TravelPeriodHolidays />
				</div>
				<div className={cn('flex flex-col gap-8 md:flex-row md:gap-24')}>
					<div className="hidden md:block">
						<TravelPeriodHolidays />
					</div>
					<div className={cn('flex items-center gap-x-8')}>
						<span className={cn('h-10 w-10 rounded-10 bg-accent-1')}></span>
						<Typography element="span" style="p">
							{translate('travel-period-pricerange-no-arrival')}
						</Typography>
					</div>
					<div className={cn('flex items-center gap-x-8')}>
						<span className={cn('h-10 w-10 rounded-10 bg-accent-5')}></span>
						<Typography element="span" style="p">
							{translate('travel-period-pricerange-no-departure')}
						</Typography>
					</div>
					{federalState && (
						<>
							<div className={cn('flex items-center gap-x-8')}>
								<span className={cn('h-10 w-10 rounded-10 bg-accent-2')}></span>
								<Typography element="span" style="p">
									{translate('travel-period-pricerange-public-holiday')}
								</Typography>
							</div>
							<div className={cn('flex items-center gap-x-8')}>
								<span className={cn('h-10 w-10 rounded-10 bg-accent-3')}></span>
								<Typography element="span" style="p">
									{translate('travel-period-pricerange-school-holiday')}
								</Typography>
							</div>
						</>
					)}
					{code && (
						<div className={cn('flex items-center gap-x-8')}>
							<span className={cn('h-10 w-10 rounded-10 bg-accent-4')}></span>
							<Typography element="span" style="p">
								{translate('travel-period-pricerange-applied-promotion')}
							</Typography>
						</div>
					)}
				</div>
				<div ref={calendarRef} className={cn('-mx-18 md:mx-0')}>
					<TravelPeriodCalendar setIsMinNightValid={setIsMinNightValid} />
				</div>
				{hotelMessages?.map((message, index) => (
					<div
						key={index}
						className={cn(
							'flex max-w-[500px] gap-x-10 rounded-4 border border-primary md:ml-[72px] md:px-16 md:py-12'
						)}
					>
						<SvgIcon iconName="info" className="mt-4 h-24 w-24 shrink-0 text-danger" />
						<Typography element="span" style="p">
							{message}
						</Typography>
					</div>
				))}
			</div>
			{showCrossSelling && (
				<div ref={crossSellBannerRef}>
					<CrossSellingBanner />
				</div>
			)}

			{
				<div
					className={cn(
						'fixed left-1/2 z-10 flex h-48 w-48 -translate-x-1/2 items-center justify-center rounded-full border border-white bg-travelPeriod-scrollHintAccent shadow-dark-light transition-[bottom] duration-500 md:hidden',
						(isStepValid && showScrollIndicator && calendarIsVisible) ||
							(showCrossSelling && showScrollIndicator)
							? 'bottom-16'
							: '-bottom-[50px]'
					)}
					onClick={() =>
						(showCrossSelling
							? crossSellBannerRef
							: proceedBtnRef
						).current?.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
						})
					}
				>
					<div className={cn('h-24 w-24 rounded-full')}>
						<SvgIcon iconName="caretDown" className="h-24 w-24 text-white" />
					</div>
				</div>
			}

			{isStepValid && (
				<div ref={proceedBtnRef} className={cn('md:ml-[auto] md:w-1/4')}>
					<Button
						data-testid="travel-period-submit"
						onClick={() => setNextStep()}
						disabled={!isStepValid}
						style="primary"
						className={cn('w-full bg-travelPeriod-button text-travelPeriod-buttonText')}
					>
						{translate('travel-period-submit-travelperiod')}
					</Button>
				</div>
			)}
		</article>
	)
}
